import type { ReactNode } from 'react';
import {
  type Permission,
  type PermissionGroup,
  type RoleKey,
  PERMISSION_GROUP,
  roleHasPermission,
} from '@gc-coach/shared';
import { RoleBadge } from '../RoleBadge/index.js';
import styles from './PermissionMatrix.module.css';

export interface RoleColumn {
  role: RoleKey;
  /** Localized role label (e.g. "Super Admin" / "Super administrateur"). */
  label: string;
  /** Live user count for this role. */
  userCount: number;
  /** Localized "USERS · LOCKED" footer text (e.g. "3 USERS · LOCKED"). Caller composes for localization. */
  meta: ReactNode;
}

export interface PermissionRow {
  permission: Permission;
  /** Localized human label (e.g. "View KB & Q&A"). */
  label: string;
}

export interface PermissionMatrixProps {
  /** Roles to render as columns. Typically the 5 fixed roles. */
  roleColumns: ReadonlyArray<RoleColumn>;
  /** Permission rows to render. Grouped automatically by PERMISSION_GROUP. */
  permissions: ReadonlyArray<PermissionRow>;
  /** Localized group labels keyed by group id (e.g. { knowledge: "KNOWLEDGE", ... }). */
  groupLabels: Record<PermissionGroup, string>;
  /** Optional extra class. */
  className?: string;
}

/**
 * Read-only roles × permissions grid for the Manage Roles view (`/admin/team/roles`).
 *
 * Per the Users handoff §4: "The 5 base roles are read-only (locked). Create
 * a custom role to tune a permission combination. Custom roles are post-MVP."
 *
 * Caller provides:
 *  - Localized role labels + user counts (the column headers)
 *  - Localized permission row labels
 *  - Localized group labels
 *
 * Component computes: which cells are ✓ vs – from the shared ROLE_PERMISSIONS
 * mapping. Cells are visually distinct (✓ green, – muted) AND text-labeled
 * for screen readers.
 */
export function PermissionMatrix({
  roleColumns,
  permissions,
  groupLabels,
  className,
}: PermissionMatrixProps) {
  // Group permissions for rendering. Preserves caller-provided order within
  // each group.
  const grouped = new Map<PermissionGroup, PermissionRow[]>();
  for (const row of permissions) {
    const group = PERMISSION_GROUP[row.permission];
    if (!grouped.has(group)) grouped.set(group, []);
    grouped.get(group)!.push(row);
  }

  return (
    <div className={[styles.wrapper, className].filter(Boolean).join(' ')}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.permHeaderCell}>{/* spacer for the permission column */}</th>
            {roleColumns.map((col) => (
              <th key={col.role} className={styles.roleHeaderCell}>
                <div className={styles.roleHeader}>
                  <RoleBadge role={col.role} label={col.label} size="md" />
                  <span className={styles.roleMeta}>{col.meta}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from(grouped.entries()).map(([group, rows]) => (
            <RoleGroup
              key={group}
              groupLabel={groupLabels[group]}
              rows={rows}
              roleColumns={roleColumns}
              colSpan={roleColumns.length + 1}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoleGroup({
  groupLabel,
  rows,
  roleColumns,
  colSpan,
}: {
  groupLabel: string;
  rows: PermissionRow[];
  roleColumns: ReadonlyArray<RoleColumn>;
  colSpan: number;
}) {
  return (
    <>
      <tr className={styles.groupRow}>
        <td colSpan={colSpan} className={styles.groupCell}>
          {groupLabel}
        </td>
      </tr>
      {rows.map((row) => (
        <tr key={row.permission} className={styles.permRow}>
          <td className={styles.permCell}>
            <span className={styles.permLabel}>{row.label}</span>
            <code className={styles.permKey}>{row.permission}</code>
          </td>
          {roleColumns.map((col) => {
            const granted = roleHasPermission(col.role, row.permission);
            return (
              <td key={col.role} className={styles.cell}>
                {granted ? (
                  <span className={styles.granted} aria-label="granted">
                    <CheckGlyph />
                  </span>
                ) : (
                  <span className={styles.notGranted} aria-label="not granted">
                    –
                  </span>
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

function CheckGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M3 7 L6 10 L11 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
